import { useState } from 'react';
import type { Fit as FitResult } from '../lib/fit-core.js';

// A SCORE WITH NO REASONS IS A NUMBER NOBODY TRUSTS.
//
// The old `fit_score` was hand-entered — Kyle typed 92 for Payra because he
// knew Payra. That works for one person on 28 accounts and for nobody else. The
// computed score has to show its working or it is just a different number to
// distrust, so the criteria are one click away and each says which way it went
// and why.
//
// Collapsed by default. The headline answers the question; the breakdown is
// there for the moment you disagree with it, which is the only moment it
// matters.

export function Fit({ fit, icpName }: { fit: FitResult | null | undefined; icpName?: string | null }) {
  const [open, setOpen] = useState(false);
  if (!fit) return null;

  const tone = fit.excluded ? 'bad'
    : fit.score == null ? 'mute'
      : fit.score >= 75 ? 'good' : fit.score >= 40 ? 'warn' : 'bad';

  return (
    <div className={`fitbox fitbox-${tone}`}>
      <button className="fithead" onClick={() => setOpen(!open)} aria-expanded={open}>
        <span className="fitnum">
          {fit.excluded ? '✕' : fit.score == null ? '—' : fit.score}
        </span>
        <span className="fitsay">
          <strong>{fit.excluded ? 'Disqualified' : 'ICP fit'}</strong>
          <span className="meta">
            {fit.summary}{icpName ? ` · ${icpName}` : ''}
          </span>
        </span>
        {fit.reasons.length > 0 && (
          <span className="fitchev" aria-hidden="true">{open ? '▾' : '▸'}</span>
        )}
      </button>

      {open && fit.reasons.length > 0 && (
        <ul className="fitwhy">
          {fit.reasons.map((r, i) => (
            <li key={i} className={`fitwhy-${r.verdict}`}>
              <span className="fitmark" aria-hidden="true">
                {r.verdict === 'hit' ? '✓' : r.verdict === 'miss' ? '✕' : '?'}
              </span>
              <strong>{r.criterion}</strong>
              <span className="meta">{r.detail}</span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

/** The compact form for a list row. */
export function FitChip({ fit }: { fit: FitResult | null | undefined }) {
  if (!fit) return null;
  if (fit.excluded) {
    return <span className="fitchip fitchip-bad" title={fit.summary}>✕</span>;
  }
  if (fit.score == null) {
    return <span className="fitchip fitchip-mute" title={fit.summary}>—</span>;
  }
  const tone = fit.score >= 75 ? 'good' : fit.score >= 40 ? 'warn' : 'bad';
  return (
    <span className={`fitchip fitchip-${tone}`} title={fit.summary}>{fit.score}</span>
  );
}
